import { type ComponentProps, memo } from 'react';

import { cn } from '@air/utils-tailwind';

import { usePlayerStore } from './PlayerContextProvider';

export type PlayerSeekbarChaptersProps = Pick<ComponentProps<'div'>, 'className'> & {
  chapters: {
    title: string;
    startTime: number;
    endTime: number;
    summary: string;
  }[];
};

export const PlayerSeekbarChapters = memo(({ chapters, className }: PlayerSeekbarChaptersProps) => {
  const currentTime = usePlayerStore((state) => state.currentTime);
  const duration = usePlayerStore((state) => state.duration);

  return (
    <div className={cn('relative flex w-full gap-0.5', className)} data-testid="PLAYER_SEEKBAR_CHAPTERS">
      {chapters.map((chapter) => {
        const chapterPercentage = ((currentTime - chapter.startTime) / (chapter.endTime - chapter.startTime)) * 100;
        const progressPercentage = Number.isNaN(chapterPercentage)
          ? 0
          : currentTime >= chapter.endTime
            ? 100
            : chapterPercentage;

        return (
          <div
            // For certain videos, the chapters do not add up to the total duration of the video. As a result, we
            // need to add `grow` to the chapter since the width of all the chapters would be less than 100%.
            className="h-1 grow overflow-hidden rounded bg-grey-7"
            data-end-time={chapter.endTime}
            data-start-time={chapter.startTime}
            key={chapter.startTime}
            style={{
              width: `${((chapter.endTime - chapter.startTime) / duration) * 100}%`,
            }}
          >
            <div
              className="size-full rounded-4 bg-blue-9"
              style={{ transform: `translateX(-${100 - progressPercentage}%)` }}
            />
          </div>
        );
      })}
    </div>
  );
});

PlayerSeekbarChapters.displayName = 'PlayerSeekbarChapters';
